import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { trackSignUp } from "@/lib/gtag";
import { trackAccountCreated } from "@/lib/ux-analytics/useMilestoneTracking";

// Explicit allowlist of trusted redirect domains
// Only exact matches are allowed - no suffix matching to prevent subdomain takeover
const TRUSTED_REDIRECT_DOMAINS = [
  "verkh.io",
  "app.verkh.io",
  "docs.verkh.io",
  "status.verkh.io",
] as const;

function isTrustedRedirectUrl(urlString: string): boolean {
  try {
    const url = new URL(urlString);
    // Only allow HTTPS redirects
    if (url.protocol !== "https:") {
      return false;
    }
    // Exact domain match only - no suffix matching
    return TRUSTED_REDIRECT_DOMAINS.includes(url.hostname as typeof TRUSTED_REDIRECT_DOMAINS[number]);
  } catch {
    return false;
  }
}

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithCode, login } = useAuthStore();
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    // No debug logging in here, deliberately.
    //
    // This component runs with the single-use exchange code, and on the
    // legacy path a session token, sitting in the URL. Logging the href or
    // the parsed search params writes both to the browser console, where
    // they persist in the tab, get captured by any error-reporting or
    // session-replay script on the page, and end up in screenshots and
    // support tickets. The code is short-lived and single-use, which limits
    // the window but does not close it.
    //
    // Genuine failures below still log. What was removed was the
    // step-by-step tracing, including the full callback URL and the code
    // and token values themselves.

    // New secure flow: exchange code for session (no token in URL)
    const code = searchParams.get("code");
    const isNewUser = searchParams.get("new") === "1";

    // Legacy support: handle old token-based redirects during migration
    const legacyToken = searchParams.get("token");

    const error = searchParams.get("error");

    if (error) {
      console.error("Auth error:", error);
      setAuthError(error);
      // Redirect to home after a short delay so user can see the error
      setTimeout(() => navigate("/"), 3000);
      return;
    }

    // New secure flow: exchange code
    if (code) {
      loginWithCode(code, isNewUser)
        .then((success) => {
          if (!success) {
            setAuthError("Authentication failed. Please try again.");
            setTimeout(() => navigate("/"), 3000);
            return;
          }

          if (isNewUser) {
            trackSignUp();
            void trackAccountCreated();
          }

          // Check for external redirect (e.g., back to docs.verkh.io)
          const externalRedirect = sessionStorage.getItem("auth_redirect");
          if (externalRedirect) {
            sessionStorage.removeItem("auth_redirect");
            // Validate it's a trusted domain before redirecting (explicit allowlist)
            if (isTrustedRedirectUrl(externalRedirect)) {
              // The auth_token cookie is set with domain: .verkh.io so it's
              // shared across all subdomains. The external site just needs to
              // call /auth/me with credentials: 'include' to authenticate.
              window.location.href = externalRedirect;
              return;
            }
          }
          navigate("/dashboard");
        })
        .catch((err) => {
          console.error("Exchange code failed:", err);
          setAuthError("Authentication failed. Please try again.");
          setTimeout(() => navigate("/"), 3000);
        });
      return;
    }

    // Legacy support: handle old token-based redirects during migration
    if (legacyToken) {
      console.warn("Using legacy token-based auth - this will be deprecated");
      login(legacyToken).then(() => {
        // Check for external redirect (e.g., back to docs.verkh.io)
        const externalRedirect = sessionStorage.getItem("auth_redirect");
        if (externalRedirect) {
          sessionStorage.removeItem("auth_redirect");
          if (isTrustedRedirectUrl(externalRedirect)) {
            const redirectWithToken = new URL(externalRedirect);
            redirectWithToken.searchParams.set("token", legacyToken);
            window.location.href = redirectWithToken.toString();
            return;
          }
        }
        navigate("/dashboard");
      });
      return;
    }

    // No code or token - redirect to home
    navigate("/");
  }, [searchParams, loginWithCode, login, navigate]);

  return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        {authError ? (
          <>
            <div className="w-8 h-8 mx-auto mb-4 text-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p className="text-red-600 mb-2">Authentication Error</p>
            <p className="text-slate-500 text-sm">{authError}</p>
            <p className="text-slate-400 text-xs mt-2">Redirecting...</p>
          </>
        ) : (
          <>
            <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-slate-600">Signing you in...</p>
          </>
        )}
      </div>
    </div>
  );
}